import { validateRuleGroupIntegrity } from '../conditional-fields/field-condition-helpers';
import { FormConfig, PageConf, ProgressBarGroup } from '@topdanmark-onlinesolutions/claim-form-types';

const verifyUniquePageIds = (pages: PageConf[]) => {
    const pageIds: { [key: string]: number } = {};
    pages.forEach((page) => {
        const { pageId } = page;

        if (pageId in pageIds) {
            pageIds[pageId]++;
        } else {
            pageIds[pageId] = 1;
        }
    });

    const pageIdExceedings = [];
    for (const pageId in pageIds) {
        const count = pageIds[pageId];

        if (count >= 2) {
            pageIdExceedings.push(pageId);
        }
    }
    if (pageIdExceedings.length) {
        throw new Error(
            `The form has not unique page ids - please check the configuration. ${JSON.stringify({ pageIdExceedings })}`
        );
    }
};

const verifyUniqueElementIds = (pages: PageConf[]) => {
    const elementIds: { [key: string]: number } = {};
    pages.forEach((page) => {
        page.elements.forEach((r) => {
            const { elementId } = r;

            if (elementId in elementIds) {
                elementIds[elementId]++;
            } else {
                elementIds[elementId] = 1;
            }
        });
    });

    const elementIdExceedings = [];
    for (const elementId in elementIds) {
        const count = elementIds[elementId];

        if (count >= 2) {
            elementIdExceedings.push(elementId);
        }
    }

    if (elementIdExceedings.length) {
        throw new Error('The following elementIds are not unique: ' + JSON.stringify(elementIdExceedings, null, 4));
    }
};

const verifyProgressBar = (pages: PageConf[], progressBarGroups: ProgressBarGroup[]) => {
    if (!progressBarGroups.length) {
        return;
    }

    pages.forEach((page) => {
        const { progressBarGroupId, showProgressBar } = page;

        if (!showProgressBar) {
            return;
        }
        if (!progressBarGroupId) {
            throw new Error('The page does not have a progressBarId: ' + JSON.stringify(page, null, 4));
        }

        const progressBarGroup = progressBarGroups.find((group) => group.progressBarGroupId === progressBarGroupId);

        if (!progressBarGroup) {
            throw new Error('The page has an unknown progressBarId: ' + JSON.stringify(page, null, 4));
        }
    });
};

const verifyAtLeastOnePageExist = (formConfig: FormConfig) => {
    if (!formConfig.pages.length) {
        throw new Error('There should be at least one page in a form: ' + JSON.stringify(formConfig, null, 4));
    }
};

const verifySummaryPageExists = (pages: PageConf[]) => {
    let hasSummaryPage = false;

    pages.forEach((page) => {
        const { pageType } = page;

        if (pageType === 'summary') {
            hasSummaryPage = true;
        }
    });

    if (!hasSummaryPage) {
        throw new Error('The form does not have a summary page');
    }
};

// Data is missing in the payload when the summerygroupId is incorrect.
const verifySummaryIdsExists = (formConfig: FormConfig) => {
    const summaryGroupIds = formConfig.summaryGroups.map((group) => group.summaryGroupId);
    const pages = formConfig.pages;

    pages.forEach((page) => {
        if (page.pageType === 'page') {
            page.elements.forEach((element) => {
                const { summaryGroupId } = element;

                if (summaryGroupId === undefined || summaryGroupIds.includes(summaryGroupId)) {
                    return;
                } else {
                    throw new Error('The page does not have a valid summaryGroupId: ' + JSON.stringify(page, null, 4));
                }
            });
        }
    });
};

const verifyRuleGroupIntegrity = (formConfig: FormConfig) => {
    formConfig.pages.forEach((page) => {
        const { elements } = page;

        elements.forEach((element) => {
            const { elementId } = element;

            validateRuleGroupIntegrity(formConfig, elementId);
        });
    });
};

const verifyEmailId = (formConfig: FormConfig) => {
    // 60785243-d132-4aba-932e-14a2c900adfa
    const hasUUIDForEmail = formConfig.automation.receiptEmail.match(
        /^[\da-z]{8}-[\da-z]{4}-[\da-z]{4}-[\da-z]{4}-[\da-z]{12}$/
    );

    if (!hasUUIDForEmail) {
        return;
    }

    throw new Error('The receiptEmail needs to be changed to a human readable one - See the other ones for the format');
};

export const checkConfigIntegrity = (formConfig: FormConfig) => {
    const { pages, progressBarGroups } = formConfig;

    verifyAtLeastOnePageExist(formConfig);
    verifyUniquePageIds(pages);
    verifyUniqueElementIds(pages);
    verifyProgressBar(pages, progressBarGroups);
    verifySummaryPageExists(pages);
    verifyRuleGroupIntegrity(formConfig);
    verifyEmailId(formConfig);
    verifySummaryIdsExists(formConfig);
};
